import { restApiLegacy } from '../../api/restApiSliceLegacy';
import { environmentApi } from '../../api/environmentApiSlice';
import { getPrerenderUser } from '../../../actions/prerenderUserActions';

export const MILLISECONDS_PER_SECOND = 1000;

export type Viewport = { width: number; height: number };

export type SettingsUpdate = Partial<{
  recachePaused: boolean;
  mobileAdaptive: boolean;
  customViewport: Viewport;
  customDesktopUserAgent: string;
  customMobileViewport: Viewport;
  customMobileUserAgent: string;
  pageLoadTimeout: number; // seconds; converted to milliseconds on the wire
  loadImages: boolean;
  renderRegion: string;
  forceHTTPS: boolean;
  emailPreferences: Record<string, unknown>;
}>;

export type UpdateSettingRequest = { userId: number } & SettingsUpdate;

// The frontend speaks seconds for pageLoadTimeout; the API speaks milliseconds
// (mirrors the legacy saveSettings thunk's `* 1000`). Only convert when present so
// a partial payload never invents a field it wasn't given.
export function buildSettingsRequestBody(update: SettingsUpdate): SettingsUpdate {
  const { pageLoadTimeout, ...rest } = update;

  return {
    ...rest,
    ...(pageLoadTimeout !== undefined ? { pageLoadTimeout: pageLoadTimeout * MILLISECONDS_PER_SECOND } : {}),
  };
}

export const settingsApi = restApiLegacy.injectEndpoints({
  endpoints: (builder) => ({
    updateSetting: builder.mutation<unknown, UpdateSettingRequest>({
      query: ({ userId, ...update }) => ({
        url: `/v1/users/${userId}/settings`,
        method: 'PUT',
        body: buildSettingsRequestBody(update),
        // dashboard-api answers this route with `res.sendStatus(200)`
        // (routes/user/v1/settings/apiv1-settings.ts) — a text/plain "OK" body, not JSON. Under
        // fetchBaseQuery's default JSON responseHandler that body fails to parse, so every
        // successful save came back as a PARSING_ERROR: the save indicator showed an error and the
        // success side effects below never ran, all while the write had actually persisted.
        responseHandler: 'text',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // A failed save must not re-seed the baseline or invalidate history; swallow the
          // rejection here so it never surfaces as an unhandled promise rejection.
          return;
        }
        // Re-seed the settings baseline and refresh audit history on every successful save.
        dispatch(getPrerenderUser());
        dispatch(environmentApi.util.invalidateTags(['EnvironmentHistory']));
      },
    }),
  }),
});

export const { useUpdateSettingMutation } = settingsApi;
